import {
  useRef,
  FC,
  HTMLAttributes,
  useLayoutEffect,
  useMemo,
  useEffect,
} from "react";
import clsx from "classnames";
import { useStyles } from "./ArrowWrapper.style";
import { useScrollHorizontal } from "../../../hooks/useScrollHorizontal";
import { Icon } from "../../core/Icon/Icon";
import { getMediaSizeScale } from "../../../models/screen";
import { isTV } from "../../../utils/platform";

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T | null) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") ref(value);
      else (ref as React.MutableRefObject<T | null>).current = value;
    });
  };
}

interface IArrowWrapper {
  size?: number;
  indexToScroll?: number;
  onBeforeArrowScroll?: () => void;
  enableScrollSnap?: boolean;
}

const ArrowWrapper: FC<HTMLAttributes<HTMLDivElement> & IArrowWrapper> = ({
  children,
  size = 30,
  indexToScroll,
  className,
  onBeforeArrowScroll,
  enableScrollSnap = false,
  ...otherProps
}) => {
  const classes = useStyles();

  const leftIconRef = useRef<HTMLDivElement>(null);
  const rightIconRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const iconSize = size * 2 * getMediaSizeScale();

  const { bindContentRef, onScrollDirection } = useScrollHorizontal({
    leftIconRef,
    rightIconRef,
  });
  const setMergedRef = useMemo(
    () => mergeRefs<HTMLDivElement>(containerRef, bindContentRef),
    [bindContentRef],
  );

  const scrollToCard = (index: number) => {
    const container = containerRef.current;
    if (!container) return;
    const child = container.children[index + 1] as HTMLElement;
    if (!child) return;
    container.scrollLeft =
      child.offsetLeft + child.offsetWidth / 2 - container.clientWidth / 2;
  };

  useLayoutEffect(() => {
    if (indexToScroll == null) return;
    scrollToCard(indexToScroll);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!enableScrollSnap || !el || isTV) return;

    let isAutoScroll = false;
    let timer: NodeJS.Timeout;

    const handleScroll = () => {
      if (isAutoScroll) return;
      clearTimeout(timer);

      timer = setTimeout(() => {
        const centerX = el.scrollLeft + el.clientWidth / 2;
        const children = Array.from(el.children) as HTMLElement[];

        // Find the closest item to the center
        const { target, dist } = children.reduce(
          (acc, child) => {
            if (child === leftIconRef.current || child === rightIconRef.current)
              return acc;
            const d = Math.abs(
              child.offsetLeft + child.offsetWidth / 2 - centerX,
            );
            return d < acc.dist ? { target: child, dist: d } : acc;
          },
          { target: null as HTMLElement | null, dist: Infinity },
        );

        // Snap if not perfectly aligned (threshold > 5px)
        if (target && dist > 5) {
          isAutoScroll = true;
          el.scrollTo({
            left:
              target.offsetLeft + target.offsetWidth / 2 - el.clientWidth / 2,
            behavior: "smooth",
          });

          // Unlock after smooth scroll animation
          setTimeout(() => (isAutoScroll = false), 450);
        }
      }, 100);
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, [enableScrollSnap]);

  const handleArrowClick = (dir: "left" | "right") => {
    onBeforeArrowScroll?.();
    onScrollDirection(dir);
  };

  return (
    <div {...otherProps} className={className} ref={setMergedRef}>
      <div
        className={classes.iconStyle}
        style={{ width: iconSize, height: iconSize, minWidth: iconSize }}
        onClick={() => handleArrowClick("left")}
        ref={leftIconRef}
      >
        <Icon size={size} name="icon-linear-arrow-left-3" />
      </div>
      {children}
      <div
        className={clsx(classes.iconStyle, classes.iconRight)}
        style={{ width: iconSize, height: iconSize, minWidth: iconSize }}
        onClick={() => handleArrowClick("right")}
        ref={rightIconRef}
      >
        <Icon size={size} name="icon-linear-arrow-right-3" />
      </div>
    </div>
  );
};

export default ArrowWrapper;
